import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/common/Layout';
import Loading from '../components/common/Loading';
import { rrhhInventarioService } from '../services/rrhhInventario';
import toast from 'react-hot-toast';
import { CalendarClock, AlertTriangle, Clock, CheckCircle2 } from 'lucide-react';

const HORIZONTES = [
    { value: 0, label: 'Solo vencidos' },
    { value: 15, label: 'Vencidos y próximos 15 días' },
    { value: 30, label: 'Vencidos y próximos 30 días' },
    { value: 90, label: 'Vencidos y próximos 90 días' },
];

const formatearFecha = (iso) => {
    if (!iso) return '—';
    const [a, m, d] = iso.slice(0, 10).split('-');
    return `${d}/${m}/${a}`;
};

const EstadoCaducidad = ({ dias }) => {
    if (dias < 0) {
        return (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                <AlertTriangle className="h-3.5 w-3.5 mr-1" /> Vencido hace {Math.abs(dias)} día(s)
            </span>
        );
    }
    return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">
            <Clock className="h-3.5 w-3.5 mr-1" /> {dias === 0 ? 'Vence hoy' : `Vence en ${dias} día(s)`}
        </span>
    );
};

const RRHHCaducidadesPage = () => {
    const navigate = useNavigate();
    const [filas, setFilas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dias, setDias] = useState(30);
    const [busqueda, setBusqueda] = useState('');

    const cargar = useCallback(async () => {
        setLoading(true);
        try {
            setFilas(await rrhhInventarioService.listarCaducidades(dias));
        } catch (err) {
            toast.error('Error al cargar las caducidades');
        } finally {
            setLoading(false);
        }
    }, [dias]);

    useEffect(() => { cargar(); }, [cargar]);

    const handleRenovado = async (fila) => {
        if (!window.confirm(`¿Cerrar la alerta de "${fila.item_descripcion}" de ${fila.empleado_nombre} sin registrar una nueva entrega? (ej. lo devolvió o ya no lo usa)`)) return;
        try {
            await rrhhInventarioService.marcarRenovado(fila.movimiento_id);
            toast.success('Alerta cerrada');
            cargar();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'No se pudo cerrar la alerta');
        }
    };

    const texto = busqueda.trim().toLowerCase();
    const visibles = texto
        ? filas.filter((f) =>
            [f.empleado_nombre, f.empleado_documento, f.item_descripcion, f.item_sku]
                .some((v) => (v || '').toLowerCase().includes(texto)))
        : filas;
    const vencidos = filas.filter((f) => f.dias_restantes < 0).length;

    return (
        <Layout>
            <div className="p-6 max-w-6xl mx-auto">
                <div className="flex items-center space-x-3 mb-6">
                    <div className="bg-amber-100 p-2.5 rounded-xl">
                        <CalendarClock className="h-6 w-6 text-amber-700" />
                    </div>
                    <div>
                        <h1 className="text-xl font-bold text-gray-900">RRHH · Caducidad EPP</h1>
                        <p className="text-sm text-gray-500">
                            Uniformes/EPP entregados que vencieron o están por vencer según la caducidad de cada producto
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="bg-white rounded-xl border border-gray-200 p-4">
                        <p className="text-xs text-gray-500 uppercase font-semibold">Vencidos</p>
                        <p className="text-2xl font-bold text-red-600">{vencidos}</p>
                    </div>
                    <div className="bg-white rounded-xl border border-gray-200 p-4">
                        <p className="text-xs text-gray-500 uppercase font-semibold">Por vencer</p>
                        <p className="text-2xl font-bold text-amber-600">{filas.length - vencidos}</p>
                    </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-[18rem_minmax(0,1fr)] gap-2 mb-4">
                    <select
                        value={dias}
                        onChange={(e) => setDias(Number(e.target.value))}
                        className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    >
                        {HORIZONTES.map((h) => <option key={h.value} value={h.value}>{h.label}</option>)}
                    </select>
                    <input
                        type="search"
                        value={busqueda}
                        onChange={(e) => setBusqueda(e.target.value)}
                        placeholder="Buscar por empleado, documento o producto..."
                        className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    />
                </div>

                {loading ? (
                    <Loading message="Cargando caducidades..." />
                ) : (
                    <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 text-sm">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Empleado</th>
                                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Producto</th>
                                    <th className="px-4 py-3 text-right font-semibold text-gray-600">Cant.</th>
                                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Entregado</th>
                                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Vence</th>
                                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Estado</th>
                                    <th className="px-4 py-3 text-right font-semibold text-gray-600">Acciones</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {visibles.length === 0 && (
                                    <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">Nada vencido ni por vencer en este rango</td></tr>
                                )}
                                {visibles.map((f) => (
                                    <tr key={f.movimiento_id} className="hover:bg-gray-50">
                                        <td className="px-4 py-3">
                                            <div className="font-medium text-gray-900">{f.empleado_nombre}</div>
                                            <div className="text-xs text-gray-400">{f.empleado_documento || ''}</div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="text-gray-900">{f.item_descripcion}</div>
                                            <div className="text-xs text-gray-400">{f.item_sku}</div>
                                        </td>
                                        <td className="px-4 py-3 text-right font-semibold text-gray-900">{f.cantidad}</td>
                                        <td className="px-4 py-3 text-gray-500">{formatearFecha(f.fecha_entrega)}</td>
                                        <td className="px-4 py-3 text-gray-500">{formatearFecha(f.fecha_vencimiento)}</td>
                                        <td className="px-4 py-3"><EstadoCaducidad dias={f.dias_restantes} /></td>
                                        <td className="px-4 py-3 text-right whitespace-nowrap">
                                            <button
                                                onClick={() => navigate('/rrhh/movimientos')}
                                                className="text-xs text-blue-600 hover:text-blue-800 mr-3"
                                                title="Registrar la nueva entrega: cierra esta alerta automáticamente"
                                            >
                                                Renovar
                                            </button>
                                            <button
                                                onClick={() => handleRenovado(f)}
                                                className="inline-flex items-center text-xs text-gray-500 hover:text-green-700"
                                                title="Cerrar alerta sin nueva entrega"
                                            >
                                                <CheckCircle2 className="h-4 w-4 mr-0.5" /> Cerrar
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                <p className="text-xs text-gray-400 mt-3">
                    Al registrar una nueva entrega del mismo producto al mismo empleado, la anterior se marca como renovada sola.
                    Los avisos por correo se configuran en Ajustes → "RRHH — Caducidad de EPP/uniformes entregados".
                </p>
            </div>
        </Layout>
    );
};

export default RRHHCaducidadesPage;
